/**
 * ModuleUpsellModal
 * 
 * Displayed when a user tries to access a module they don't have permission for.
 * Encourages them to upgrade their plan to unlock the module.
 */

import { X, Lock, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ModuleUpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleName: string;
  moduleNameHe: string;
}

export function ModuleUpsellModal({
  isOpen,
  onClose,
  moduleNameHe,
}: ModuleUpsellModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleViewPlans = () => {
    onClose();
    // Navigate to landing page and scroll to pricing
    navigate('/');
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing-section');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors z-10"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>

          {/* Header with gradient background */}
          <div className="bg-gradient-to-br from-primary-600 to-primary-800 px-6 pt-8 pb-6 text-white">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <Lock className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">מודול נעול</h2>
                <p className="text-primary-100 text-sm">
                  {moduleNameHe}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-right">
                אינך מנוי על מודול זה
              </h3>
              <p className="text-gray-600 text-right leading-relaxed">
                מודול <span className="font-semibold text-gray-900">{moduleNameHe}</span> זמין
                רק למשתמשים בתוכניות המתקדמות של Axelio.
              </p>
              <p className="text-gray-600 text-right leading-relaxed mt-2">
                שדרגו את התוכנית שלכם כדי לפתוח גישה לכל התכונות והיכולות המתקדמות.
              </p>
            </div>

            {/* Benefits */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <TrendingUp className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-right">
                  <p className="text-sm font-semibold text-amber-900 mb-1">
                    יתרונות השדרוג
                  </p>
                  <ul className="text-sm text-amber-800 space-y-1">
                    <li>• גישה לכל המודולים של Axelio</li>
                    <li>• אוטומציה מתקדמת לניהול עסקי</li>
                    <li>• דוחות ותובנות בזמן אמת</li>
                    <li>• תמיכה מועדפת</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={handleViewPlans}
                className="w-full px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                לפרטים והרשמה
              </button>
              <button
                onClick={onClose}
                className="w-full px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
